"use client"

import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { ThumbsUp, ThumbsDown } from "lucide-react"
import { generateVoteFingerprint } from "@/lib/community/vote-fingerprint"
import { toast } from "sonner"
import { communityAnalytics } from "@/lib/analytics"

// Animated counter component
function AnimatedCounter({ value, compact = false }: { value: number; compact?: boolean }) {
  const [displayValue, setDisplayValue] = useState(value)
  const [isAnimating, setIsAnimating] = useState(false)
  const previousValue = useRef(value)

  useEffect(() => {
    if (previousValue.current === value) return

    setIsAnimating(true)
    const start = previousValue.current
    const end = value
    const duration = 300 // ms
    const startTime = Date.now()

    const animate = () => {
      const now = Date.now()
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3)
      const current = Math.round(start + (end - start) * easeOut)

      setDisplayValue(current)

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        setIsAnimating(false)
        previousValue.current = value
      }
    }

    requestAnimationFrame(animate)
  }, [value])

  return (
    <>
      <span
        aria-hidden="true"
        className={`font-semibold min-w-8 text-center transition-all ${
          compact ? 'text-sm' : 'text-base'
        } ${
          isAnimating ? 'scale-125 text-green-600' : 'scale-100'
        }`}
        style={{
          transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.3s ease'
        }}
      >
        {displayValue}
      </span>
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        Current score: {value}
      </span>
    </>
  )
}

interface VoteButtonsProps {
  photoId: string
  currentScore: number
  compact?: boolean
  onVoteUpdated?: (update: VoteUpdate) => void
}

export interface VoteUpdate {
  score: number
  upvotes: number
  downvotes: number
  action: 'added' | 'changed' | 'removed'
  userVote: 'upvote' | 'downvote' | null
}

export function VoteButtons({
  photoId,
  currentScore,
  compact = false,
  onVoteUpdated,
}: VoteButtonsProps) {
  const [userVote, setUserVote] = useState<'upvote' | 'downvote' | null>(null)
  const [score, setScore] = useState(currentScore)
  const [isVoting, setIsVoting] = useState(false)
  const [initializedPhotoId, setInitializedPhotoId] = useState<string | null>(null)
  const voteMutationVersion = useRef(0)

  useEffect(() => {
    setScore(currentScore)
  }, [currentScore])

  useEffect(() => {
    let cancelled = false
    const mutationVersionAtStart = voteMutationVersion.current

    setInitializedPhotoId(null)
    setUserVote(null)

    const loadVote = async () => {
      try {
        const fingerprint = await generateVoteFingerprint()
        const response = await fetch(`/api/community/photos/${photoId}/vote`, {
          headers: { 'X-Voter-Fingerprint': fingerprint },
        })
        if (!response.ok || cancelled) return
        const data = await response.json()
        if (!cancelled && voteMutationVersion.current === mutationVersionAtStart) {
          setUserVote(data.voteType === 'upvote' || data.voteType === 'downvote' ? data.voteType : null)
        }
      } catch {
        // Vote status is progressive enhancement. The write path still returns
        // the authoritative state if this read is unavailable.
      } finally {
        if (!cancelled) {
          setInitializedPhotoId(photoId)
        }
      }
    }

    void loadVote()
    return () => { cancelled = true }
  }, [photoId])

  const handleVote = async (voteType: 'upvote' | 'downvote') => {
    if (isVoting || initializedPhotoId !== photoId) return

    try {
      setIsVoting(true)
      voteMutationVersion.current += 1

      // Generate fingerprint for anonymous voting
      const fingerprint = await generateVoteFingerprint()

      const response = await fetch(`/api/community/photos/${photoId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Voter-Fingerprint': fingerprint
        },
        body: JSON.stringify({ voteType })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to vote')
      }

      const data = await response.json()

      const action = data.action === 'removed' || data.action === 'changed'
        ? data.action
        : 'added'
      const nextUserVote = action === 'removed' ? null : voteType

      if (typeof data.score === 'number') {
        setScore(data.score)
      }

      setUserVote(nextUserVote)
      communityAnalytics.voteCast(voteType, photoId, action)

      if (
        typeof data.score === 'number'
        && typeof data.upvotes === 'number'
        && typeof data.downvotes === 'number'
      ) {
        onVoteUpdated?.({
          score: data.score,
          upvotes: data.upvotes,
          downvotes: data.downvotes,
          action,
          userVote: nextUserVote,
        })
      }

      switch (action) {
        case 'removed':
          toast.success(`${voteType === 'upvote' ? 'Upvote' : 'Downvote'} removed!`)
          break
        case 'changed':
          toast.success(`Changed to ${voteType === 'upvote' ? 'upvote' : 'downvote'}!`)
          break
        case 'added':
        default:
          toast.success(voteType === 'upvote' ? 'Upvoted!' : 'Downvoted!')
          break
      }
    } catch (error) {
      console.error('Vote error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to vote')
    } finally {
      setIsVoting(false)
    }
  }

  const buttonSize = compact ? "sm" : "default"
  const iconSize = compact ? "w-4 h-4" : "w-5 h-5"
  const isVoteStatusInitialized = initializedPhotoId === photoId

  return (
    <div className="flex items-center gap-2">
      <Button
        variant={userVote === 'upvote' ? 'default' : 'outline'}
        size={buttonSize}
        onClick={() => handleVote('upvote')}
        disabled={isVoting || !isVoteStatusInitialized}
        aria-label={compact ? 'Upvote photo' : undefined}
        aria-pressed={userVote === 'upvote'}
        className={`transition-colors ${compact ? 'min-h-[44px] min-w-[44px]' : ''} ${userVote === 'upvote' ? 'bg-green-600 hover:bg-green-700' : ''}`}
      >
        <ThumbsUp className={iconSize} aria-hidden="true" />
        {!compact && <span className="ml-1">Upvote</span>}
      </Button>

      <AnimatedCounter value={score} compact={compact} />

      <Button
        variant={userVote === 'downvote' ? 'default' : 'outline'}
        size={buttonSize}
        onClick={() => handleVote('downvote')}
        disabled={isVoting || !isVoteStatusInitialized}
        aria-label={compact ? 'Downvote photo' : undefined}
        aria-pressed={userVote === 'downvote'}
        className={`transition-colors ${compact ? 'min-h-[44px] min-w-[44px]' : ''} ${userVote === 'downvote' ? 'bg-gray-600 hover:bg-gray-700' : ''}`}
      >
        <ThumbsDown className={iconSize} aria-hidden="true" />
        {!compact && <span className="ml-1">Downvote</span>}
      </Button>
    </div>
  )
}
